import {
  Axis,
  BaseDataSource,
  BaseSeries,
  ChartData,
  DataSource,
  LineSeries,
  PointShape,
  RangeSeries,
  RawChart,
  RawDataSource,
  ReferenceLine,
  ScatterSeries,
  Series,
  XAxis,
  YAxis
} from "interfaces/chart";
import Highcharts from "highcharts";
import { useEffect, useState } from "react";

//region default settings
/**
 * Adds defaults to the chart options
 * Currently this only needs to be applied when first creating the state, as they will be maintained when spreading the previous state
 * @param chartOptions
 */
const addChartDefaults = (
  chartOptions: Highcharts.Options
): Highcharts.Options => {
  // maintains the default chart options when overrides are not specifically set

  return {
    series: [],
    ...chartOptions,
    // Add spacing to chart so that exports have padding
    plotOptions: {
      line: {
        // FIXME: turned off while immutable is set to true, otherwise causes series labels to flash
        animation: false
      }
    },
    chart: {
      // FIXME: turned off while immutable is set to true, otherwise causes lines to redraw
      animation: false,
      zooming: {
        type: "xy",
        resetButton: {
          position: {
            align: "left"
          }
        }
      },
      spacingTop: 24,
      spacingRight: 24,
      spacingBottom: 24,
      spacingLeft: 24,
      // replace default chart options with new chart options if available
      ...chartOptions?.chart
    },
    legend: {
      align: "left",
      itemDistance: 32
    },
    // get rid of the highchart logo
    credits: {
      enabled: false,
      ...chartOptions?.credits
    },
    // Show only 2 decimal points on the graph
    // %a days in text, %d date 2digit, %e date, %b months in text, %m months in number
    tooltip: {
      valueDecimals: 2,
      xDateFormat: "%A, %d %b %Y, %H:%M",
      ...chartOptions?.tooltip
    },
    // Remove exporting hamburger and default export resolution
    exporting: {
      enabled: false,
      sourceWidth: 1200,
      sourceHeight: 800,
      scale: 1,
      ...chartOptions?.exporting
    }
  };
};
//endregion

//region map titles
/**
 * Converts a title into a highcharts title object
 * @param title the title of the chart
 */
export const getTitleOptions = (title?: string): Highcharts.TitleOptions => ({
  text: title
});

/**
 * Converts a subtitle into a highcharts subtitle object
 * @param subtitle the subtitle of the chart
 */
export const getSubtitleOptions = (
  subtitle?: string | null
): Highcharts.SubtitleOptions | undefined =>
  subtitle ? { text: subtitle } : undefined;
//endregion

//region map axes
type HighchartsAxisLimitOptions = Pick<
  Highcharts.AxisOptions,
  "min" | "max" | "startOnTick" | "endOnTick"
>;

type HighchartsAxisOptions = HighchartsAxisLimitOptions &
  Pick<Highcharts.AxisOptions, "type" | "title">;

/**
 * Converts linear axis options into Highchart Axis options
 * @param axis AI Hub axis object (with type linear)
 */
const getLinearAxisTypeOptions = (axis: Axis): HighchartsAxisLimitOptions => {
  let min: number | undefined = undefined;
  let max: number | undefined = undefined;
  let startOnTick = undefined;
  let endOnTick = undefined;

  if (typeof axis.min === "number" || axis.min === null) {
    min = Number(axis.min);
    // forces min to be the specifically set minimum
    startOnTick = false;
  } else {
    console.error(
      `min provided (${axis.min}) is not valid, so will be ignored`
    );
  }

  if (axis.max) {
    if (typeof axis.max === "number") {
      max = Number(axis.max);
      endOnTick = false;
    } else {
      console.error(
        `max provided (${axis.max}) is not valid, so will be ignored`
      );
    }
  }

  return { min, max, startOnTick, endOnTick };
};

/**
 * Converts the datetime axis into Highcharts Axis options
 * @param axis AI Hub axis object (with type datetime)
 */
const getDatetimeAxisTypeOptions = (axis: Axis): HighchartsAxisLimitOptions => {
  let min: number | undefined = undefined;
  let max: number | undefined = undefined;
  let startOnTick = undefined;
  let endOnTick = undefined;

  if (typeof axis.min === "string") {
    const minDate = new Date(axis.min);
    if (minDate.toString() !== "Invalid date") {
      min = minDate.getTime();
      // forces min to be the specifically set minimum
      startOnTick = false;
    } else {
      console.error(
        `The string provided for min in axis of id ${axis.id} is invalid, it should be a datetime. Instead received: ${axis.min}`
      );
    }
  } else {
    console.error(
      `min provided (${axis.min}) is not valid, so will be ignored`
    );
  }

  if (axis.max) {
    if (typeof axis.max === "string") {
      const maxDate = new Date(axis.max);
      if (maxDate.toString() !== "Invalid date") {
        max = maxDate.getTime();
        // forces max to be the specifically set maximum
        endOnTick = false;
      } else {
        console.error(
          `The string provided for max in axis of id ${axis.id} is invalid, it should be a datetime. Instead received: ${axis.max}`
        );
      }
    } else {
      console.error(
        `max provided (${axis.max}) is not valid, so will be ignored`
      );
    }
  }

  return { min, max, startOnTick, endOnTick };
};

/**
 * Gets the specific highchart options for a specific axis type
 * @param axis An AI Hub axis object
 */
const getAxisTypeOptions = (axis: Axis): HighchartsAxisLimitOptions => {
  if (axis.min || axis.max) {
    switch (axis.type) {
      case "linear":
        return getLinearAxisTypeOptions(axis);
      case "datetime":
        return getDatetimeAxisTypeOptions(axis);
      default:
        console.error(`Invalid axis type of ${axis.type}`);
    }
  }

  return {
    min: undefined,
    max: undefined,
    startOnTick: true,
    endOnTick: true
  };
};

/**
 * Gets the highcharts options for an AI Hub axis object which are type independent
 * @param axis
 */
function getGenericAxisOptions(axis: Axis): HighchartsAxisOptions {
  const axisTypeOptions = getAxisTypeOptions(axis);

  return {
    ...axisTypeOptions,
    type: axis.type,
    title: {
      text: axis.title.enabled ? axis.title.text : undefined
    }
  };
}

/**
 * Gets the highchart options for the X Axis objects provided
 * @param xAxes a list of AI Hub X Axis objects to convert
 * @param plotLines is Reference line for Axis
 */
export const getXAxesOptions = (
  xAxes: XAxis[],
  plotLines?: ReferenceLine[]
): Highcharts.XAxisOptions[] => {
  return xAxes.map<Highcharts.XAxisOptions>(axis => {
    const opposite = axis.alignment === "top";
    const axisOptions = getGenericAxisOptions(axis);

    const plotOptions: any[] = [];
    plotLines?.forEach(plot => {
      if (plot.targetAxis === axis.id) {
        plotOptions.push({
          color: plot.color,
          width: plot.width,
          value: plot.value,
          dashStyle: plot.dashStyle,
          zIndex: 4,
          label: {
            text: plot.title
          }
        });
      }
    });

    return {
      ...axisOptions,
      // index: axis.id,
      opposite,
      dateTimeLabelFormats: {
        day: { main: "%e %b %y" },
        week: { main: "%e %b %y" },
        month: { main: "%b %y" },
        year: { main: "%b %y" }
      },
      plotLines: [...plotOptions]
    };
  });
};

/**
 * Gets the highchart options for the AI Hub Y Axis objects provided
 * @param yAxes a list of AI Hub Y Axis objects to convert
 * @param plotLines is Reference line for Axis
 */
export const getYAxesOptions = (
  yAxes: YAxis[],
  plotLines?: ReferenceLine[]
): Highcharts.YAxisOptions[] => {
  return yAxes.map<Highcharts.YAxisOptions>(axis => {
    const opposite = axis.alignment === "right";
    const axisOptions = getGenericAxisOptions(axis);

    const plotOptions: any[] = [];
    plotLines?.forEach(plot => {
      if (plot.targetAxis === axis.id) {
        plotOptions.push({
          color: plot.color,
          width: plot.width,
          value: plot.value,
          dashStyle: plot.dashStyle,
          zIndex: 4,
          label: {
            text: plot.title,
            align: "right"
          }
        });
      }
    });

    return {
      ...axisOptions,
      minRange: 10,
      opposite,
      plotLines: [...plotOptions]
    };
  });
};
//endregion

//region map data sources
/**
 * Gets data from the datasource depending on what type of data source it is
 * @param dataSource the AI Hub data source object to get data from
 */
const getDataFromSpecifictype = (
  dataSource: BaseDataSource
): [number, number][] | [number, number, number][] => {
  if (dataSource.type === "raw") {
    return (dataSource as RawDataSource).args.data;
  } else {
    console.error(`can't handle ${dataSource.type} currently`);
    return [];
  }
};
//endregion

//region map series

export const COLOR_PALETTE = [
  "#1f77b4",
  "#ff7f0e",
  "#2ca02c",
  "#d62728",
  "#9467bd",
  "#8c564b",
  "#e377c2",
  "#7f7f7f",
  "#bcbd22",
  "#17becf",
  "#aec7e8",
  "#ffbb78",
  "#98df8a",
  "#ff9896",
  "#c5b0d5",
  "#c49c94",
  "#f7b6d2",
  "#c7c7c7",
  "#dbdb8d",
  "#9edae5"
];

/**
 * Gets the color for the series based on the colour palette
 * @param colorPalette the color palette of the chart
 * @param index the index of the series in the series array
 */
const getSeriesColor = (colorPalette: string[], index: number): string => {
  return colorPalette[index % colorPalette.length];
};

/**
 * Extracts the series options that every highcharts series have
 * @param series the series to extract highcharts options from
 * @param xAxes the x axes of the chart
 * @param yAxes the y axes of the chart
 */
const getGenericSeriesOptions = (
  series: BaseSeries<BaseDataSource>,
  xAxes: XAxis[],
  yAxes: YAxis[]
): Partial<Highcharts.SeriesOptions> => {
  const xAxisIndex = xAxes.findIndex(xAxis => xAxis.id === series.xAxisId);
  const yAxisIndex = yAxes.findIndex(yAxis => yAxis.id === series.yAxisId);

  if (xAxisIndex === -1) {
    console.error(
      `Could not match "${series.title}" to x axis with id ${series.xAxisId}. Axis will be implicit, which could cause unexpected behaviour.`
    );
  }
  if (yAxisIndex === -1) {
    console.error(
      `Could not match "${series.title}" to y axis with id ${series.yAxisId}. Axis will be implicit, which could cause unexpected behaviour.`
    );
  }

  return {
    name: series.title,
    xAxis: xAxisIndex,
    yAxis: yAxisIndex
  };
};

/**
 * gets the highchart options for the points
 * @param pointWidth the width the points should be
 * @param pointShape the shape the points should be
 * @param pointsEnabled whether or not to show the points
 */
const getMarkerOptions = (
  pointWidth?: number,
  pointShape?: PointShape,
  pointsEnabled: boolean = true
): Highcharts.PointMarkerOptionsObject => {
  return {
    enabled: pointsEnabled,
    radius: pointWidth ?? 4,
    symbol: pointShape
  };
};

/**
 * Converts the AI Hub line series object into a highcharts series object
 * @param series The AI Hub line series object to convert
 * @param color the color to display the series
 */
const getLineSeriesOptions = (
  series: LineSeries<BaseDataSource>,
  color: string
): Highcharts.SeriesLineOptions => {
  const seriesData = getDataFromSpecifictype(series.dataSource);

  return {
    type: "line",
    data: seriesData,
    color,
    marker: getMarkerOptions(
      series.pointWidth,
      series.pointShape,
      series.pointsEnabled
    ),
    lineWidth: series.lineWidth ?? 2
  };
};

/**
 * Converts an AI Hub scatter series into a highcharts series object
 * @param series The AI Hub scatter series object to convert
 * @param color the color to display the series
 */
const getScatterSeriesOptions = (
  series: ScatterSeries<BaseDataSource>,
  color: string
): Highcharts.SeriesScatterOptions => {
  const seriesData = getDataFromSpecifictype(series.dataSource);

  return {
    type: "scatter",
    data: seriesData,
    color,
    marker: getMarkerOptions(series.pointWidth, series.pointShape)
  };
};

function getRangeSeriesOptions(
  series: RangeSeries<DataSource>,
  color: string
): Highcharts.SeriesArearangeOptions {
  const seriesData = getDataFromSpecifictype(series.dataSource);

  return {
    type: "arearange",
    data: seriesData,
    color,
    fillOpacity: 0.75, // TODO: figure out why this doesn't really work
    opacity: 0.75,
    zIndex: -99999,
    connectNulls: true
  };
}

/**
 * Gets the specific highcharts options depending on the type of series provided
 * @param series the AI Hub series to convert
 * @param color the color to display the series
 */
const getSeriesTypeOptions = (
  series: Series<DataSource>,
  color: string
): Highcharts.SeriesOptionsType => {
  switch (series.seriesType) {
    case "line":
      return getLineSeriesOptions(series, color);
    case "scatter":
      return getScatterSeriesOptions(series, color);
    case "range":
      return getRangeSeriesOptions(series, color);
    default:
      console.error(`unsupported series type`);
      return { type: "line" } as Highcharts.SeriesLineOptions;
  }
};

const combineOptions = <T extends Highcharts.SeriesOptionsType>(
  genericSeriesOptions: Partial<Highcharts.SeriesOptions>,
  specificTypeOptions: T
): T => {
  return { ...genericSeriesOptions, ...specificTypeOptions };
};

/**
 * Maps a list of AI Hub series objects into a list of Highcharts series options
 * @param series the list of AI Hub series objects to convert
 * @param xAxes the x axes of the chart
 * @param yAxes the y axes of the chart
 * @param colorPalette the color palette for the chart, used to determine the color of each of the series
 */
export const getSeriesOptions = (
  series: Series<DataSource>[],
  xAxes: XAxis[],
  yAxes: YAxis[]
): Highcharts.SeriesOptionsType[] => {
  return series.map<Highcharts.SeriesOptionsType>(series => {
    const genericSeriesOptions = getGenericSeriesOptions(series, xAxes, yAxes);

    let seriesColor = "";
    if (series.color) {
      seriesColor = series.color;
    } else {
      seriesColor = getSeriesColor(COLOR_PALETTE, series.colorIndex);
    }

    const seriesTypeOptions = getSeriesTypeOptions(series, seriesColor);

    return combineOptions(genericSeriesOptions, seriesTypeOptions);
  });
};
//endregion

/**
 * Converts a AI Hub chart object into a Highcharts chart options object
 * @param chart the AI Hub chart object to be converted
 */
export const convertAIHubChartToHighchartOptions = (
  chart: ChartData
): Highcharts.Options => {
  const title = getTitleOptions(chart.title);
  const subtitle = getSubtitleOptions(chart.subtitle);
  const xAxis = getXAxesOptions(chart.xAxes, chart.plotLines || undefined);
  const yAxis = getYAxesOptions(chart.yAxes, chart.plotLines || undefined);
  const series = getSeriesOptions(chart.series, chart.xAxes, chart.yAxes);

  return {
    title,
    subtitle,
    xAxis,
    yAxis,
    series
  };
};

export const useMappedChartOptions = (chart: RawChart): Highcharts.Options => {
  const [mappedChart, setMappedChart] = useState(() =>
    addChartDefaults(convertAIHubChartToHighchartOptions(chart))
  );

  useEffect(() => {
    setMappedChart(prevState => ({
      ...prevState,
      title: getTitleOptions(chart.title || undefined)
    }));
  }, [chart.title]);

  useEffect(() => {
    setMappedChart(prevState => {
      return { ...prevState, subtitle: getSubtitleOptions(chart.subtitle) };
    });
  }, [chart.subtitle]);

  useEffect(() => {
    setMappedChart(prevState => {
      return { ...prevState, xAxis: getXAxesOptions(chart.xAxes) };
    });
  }, [chart.xAxes]);

  useEffect(() => {
    setMappedChart(prevState => {
      return { ...prevState, yAxis: getYAxesOptions(chart.yAxes) };
    });
  }, [chart.yAxes]);

  useEffect(() => {
    setMappedChart(prevState => {
      return {
        ...prevState,
        series: getSeriesOptions(chart.series, chart.xAxes, chart.yAxes)
      };
    });
  }, [chart.series, chart.xAxes, chart.yAxes]);

  useEffect(() => {
    setMappedChart(prevState => {
      return {
        ...prevState,
        xAxis: getXAxesOptions(chart.xAxes, chart.plotLines || undefined),
        yAxis: getYAxesOptions(chart.yAxes, chart.plotLines || undefined)
      };
    });
  }, [chart.plotLines, chart.xAxes, chart.yAxes]);

  return mappedChart;
};
